import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { FileText, Gauge } from "lucide-react";
import { CVBuilder } from "@/components/cv/CVBuilder";
import { AdminAtsScanner } from "@/components/cv/AdminAtsScanner";
import { getMyProfile } from "@/lib/api/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/cv")({
  validateSearch: (s: Record<string, unknown>) => ({
    template: typeof s.template === "string" ? s.template : undefined,
    tab: s.tab === "ats" ? ("ats" as const) : ("builder" as const),
  }),
  beforeLoad: async () => {
    const profile = await getMyProfile();
    if (!profile?.isAdmin) {
      throw redirect({ to: "/portal" });
    }
  },
  component: AdminCVBuilderPage,
});

function AdminCVBuilderPage() {
  const { template, tab: initialTab } = Route.useSearch();
  const [tab, setTab] = useState<"builder" | "ats">(initialTab);

  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="Admin CV tools"
        className="sticky top-0 z-10 -mx-4 flex gap-1 border-b border-border bg-background/95 px-4 py-2 backdrop-blur sm:mx-0 sm:rounded-lg sm:border sm:px-2"
      >
        <TabButton
          active={tab === "builder"}
          onClick={() => setTab("builder")}
          icon={<FileText className="h-4 w-4" aria-hidden="true" />}
          label="CV Builder (unbranded)"
        />
        <TabButton
          active={tab === "ats"}
          onClick={() => setTab("ats")}
          icon={<Gauge className="h-4 w-4" aria-hidden="true" />}
          label="ATS Scanner"
        />
      </div>

      {tab === "builder" ? (
        <div role="tabpanel" aria-label="CV Builder">
          <CVBuilder
            unbranded
            presetTemplate={template}
            title="CV Builder — Admin (unbranded)"
            subhead={
              <p className="mt-1 text-sm text-foreground/70">
                Build polished, ATS-friendly CVs for clients without the firm's
                footer. The PDF is a clean, white-label document ready for
                client delivery. All other features — import, templates, AI
                generation, SA suggestions — work identically to the public
                builder.
              </p>
            }
          />
        </div>
      ) : (
        <div role="tabpanel" aria-label="ATS Scanner">
          <AdminAtsScanner />
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 ${
        active
          ? "bg-navy text-white shadow-sm"
          : "text-navy hover:bg-muted"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
